import type { PlatformTypeName } from "@multi-chat/shared";
import { FeedBotLogo } from "../../../components/brand/FeedBotLogo.js";
import { ChannelIcon } from "../../../components/channel/ChannelIcon.js";
import { ChannelMark } from "../../../components/channel/ChannelMark.js";
import { channelBadgeClassName } from "../../../components/channel/channel-presentation.js";
import { motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { authEntryStageMotion } from "./auth-entry-motion.js";

/** Illustration rows: WhatsApp, Messenger, Instagram. */
const HERO_CHANNELS: PlatformTypeName[] = ["WHATSAPP", "FACEBOOK", "INSTAGRAM"];

/** Mobile band, physical left to right in both directions. */
const MOBILE_CHANNELS: PlatformTypeName[] = ["INSTAGRAM", "FACEBOOK", "WHATSAPP"];

/**
 * Decorative workspace illustration (rail, list, thread, composer).
 * Abstract placeholders only: no names, previews, or counts.
 */
function ConversationComposition({ compact = false }: { compact?: boolean }): React.JSX.Element {
  return (
    <div
      className={cn("relative w-full text-start", compact ? "max-w-[300px]" : "max-w-[470px]")}
      aria-hidden="true"
    >
      {compact ? null : (
        <div className="absolute inset-x-6 -top-3 bottom-3 rounded-[17px] border border-[rgba(219,255,232,0.08)] bg-white/[0.03]" />
      )}

      <div
        className={cn(
          "relative overflow-hidden border border-[rgba(219,255,232,0.17)] bg-white/[0.055] shadow-[0_30px_80px_#0003] backdrop-blur-[10px]",
          compact ? "rounded-[14px]" : "rounded-[17px]",
        )}
      >
        <div className={cn("flex", compact ? "h-[120px]" : "min-h-[15rem]")}>
          {compact ? null : (
            <div className="flex w-11 shrink-0 flex-col items-center gap-2.5 border-e border-white/8 bg-black/20 py-3.5">
              <span className="size-6 rounded-md bg-brand" />
              <span className="size-6 rounded-md bg-white/10" />
              <span className="size-6 rounded-md bg-white/10" />
            </div>
          )}

          <div
            className={cn(
              "grid w-[44%] shrink-0 content-start border-e border-white/8",
              compact ? "gap-1 px-1.5 py-2" : "gap-1.5 px-2.5 py-3.5",
            )}
          >
            {HERO_CHANNELS.map((platformType, row) => (
              <div
                key={platformType}
                className={cn(
                  "flex items-center rounded-[10px]",
                  compact ? "gap-1.5 px-1.5 py-1" : "gap-2 px-2 py-2",
                  row === 0 ? "bg-brand/15 ring-1 ring-brand/25" : "bg-transparent",
                )}
              >
                <span
                  className={cn(
                    "relative inline-grid shrink-0 place-items-center rounded-full bg-white/12",
                    compact ? "size-6" : "size-7",
                  )}
                >
                  <span
                    className={cn(
                      "absolute -end-0.5 -bottom-0.5 inline-grid place-items-center rounded-full text-white",
                      compact ? "size-3" : "size-3.5",
                      channelBadgeClassName(platformType),
                    )}
                  >
                    <ChannelIcon
                      platformType={platformType}
                      size={compact ? 7 : 8}
                      className="text-white"
                    />
                  </span>
                </span>
                <span className="grid min-w-0 flex-1 gap-1">
                  <span className="block h-1.5 w-3/4 rounded-full bg-white/25" />
                  <span className="block h-1.5 w-1/2 rounded-full bg-white/12" />
                </span>
              </div>
            ))}
          </div>

          <div
            className={cn(
              "flex min-w-0 flex-1 flex-col",
              compact ? "gap-1.5 px-2 py-2" : "gap-2 px-3 py-3.5",
            )}
          >
            <div
              className={cn(
                "max-w-[80%] rounded-2xl rounded-es-md border border-white/10 bg-white/8",
                compact ? "px-2 py-1.5" : "px-3 py-2.5",
              )}
            >
              <span className="block h-1.5 w-full rounded-full bg-white/25" />
              <span className="mt-1.5 block h-1.5 w-3/5 rounded-full bg-white/15" />
            </div>
            <div
              className={cn(
                "ms-auto w-[72%] rounded-2xl rounded-ee-md bg-brand-primary/85",
                compact ? "px-2 py-1.5" : "px-3 py-2.5",
              )}
            >
              <span className="block h-1.5 w-full rounded-full bg-white/45" />
              <span className="mt-1.5 block h-1.5 w-2/3 rounded-full bg-white/25" />
            </div>
            {compact ? null : (
              <div className="max-w-[60%] rounded-2xl rounded-es-md border border-white/10 bg-white/8 px-3 py-2.5">
                <span className="block h-1.5 w-full rounded-full bg-white/20" />
              </div>
            )}
            <div
              className={cn(
                "mt-auto flex items-center gap-2 rounded-full border border-white/10 bg-black/20",
                compact ? "px-2 py-1" : "px-3 py-2",
              )}
            >
              <span className="block h-1.5 flex-1 rounded-full bg-white/12" />
              <span
                className={cn(
                  "inline-block shrink-0 rounded-full bg-brand",
                  compact ? "size-3" : "size-4",
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AuthEntryVisualProps {
  title: string;
  description: string;
  logoLabel: string;
  /** Mobile band: provider icons and a short illustration, not the full hero. */
  compact?: boolean;
  /** Fills a viewport-locked shell instead of setting its own minimum height. */
  fitViewport?: boolean;
}

export function AuthEntryVisual({
  title,
  description,
  logoLabel,
  compact = false,
  fitViewport = false,
}: AuthEntryVisualProps): React.JSX.Element {
  const reduceMotion = useReducedMotion() ?? false;
  const stage = authEntryStageMotion(reduceMotion);

  if (compact) {
    return (
      <div className="relative isolate overflow-hidden bg-[radial-gradient(ellipse_at_35%_0%,#184a3c,#0b2726_62%,#091d1d)] px-4 pt-[max(0.85rem,env(safe-area-inset-top,0px))] pb-4 text-white">
        <div
          className="pointer-events-none absolute -start-[18%] -top-24 size-[280px] rounded-full border border-[rgba(135,221,188,0.07)] shadow-[0_0_0_48px_rgba(152,221,177,0.03)]"
          aria-hidden="true"
        />
        <div className="relative flex items-center justify-between gap-3">
          <FeedBotLogo
            tone="onDark"
            animated
            label={logoLabel}
            className="h-8 w-[148px]"
          />
          <div dir="ltr" className="flex shrink-0 items-center gap-1.5">
            {MOBILE_CHANNELS.map((platformType) => (
              <ChannelMark
                key={platformType}
                platformType={platformType}
                size="nav"
                shape="circle"
                className="size-7"
              />
            ))}
          </div>
        </div>
        <div className="relative mt-3 flex justify-center">
          <ConversationComposition compact />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative isolate flex h-full min-h-dvh flex-col overflow-hidden bg-[radial-gradient(ellipse_at_35%_10%,#184a3c,#0b2726_55%,#091d1d)] px-8 py-12 text-white lg:px-12",
        fitViewport && "fit-desktop:min-h-0 short-desktop:py-8",
      )}
    >
      <div
        className="pointer-events-none absolute top-[-43%] left-[-40%] size-[660px] rounded-full border border-[rgba(135,221,188,0.07)] shadow-[0_0_0_86px_rgba(152,221,177,0.026),0_0_0_184px_rgba(152,221,177,0.018)]"
        aria-hidden="true"
      />
      <motion.div
        className="relative m-auto flex w-full max-w-[570px] flex-col items-center text-center"
        initial="hidden"
        animate="show"
        variants={stage.container}
      >
        <motion.div variants={stage.item}>
          <FeedBotLogo
            tone="onDark"
            animated
            label={logoLabel}
            className="mb-[33px] w-[260px]"
          />
        </motion.div>

        <motion.div className="grid justify-items-center" variants={stage.item}>
          <h1 className="m-0 mb-[18px] text-[clamp(34px,3.3vw,54px)] leading-[1.17] font-[700] tracking-[-0.06em] whitespace-pre-line text-white">
            {title}
          </h1>
          <p className="m-0 mb-[30px] max-w-[450px] text-[16px] leading-[1.8] text-[#a8c6bd] wrap-anywhere">
            {description}
          </p>
        </motion.div>

        <motion.div className="flex w-full justify-center" variants={stage.item}>
          <ConversationComposition />
        </motion.div>
      </motion.div>
    </div>
  );
}
